import { useCallback, useState } from 'react'

import { Heading } from '@circlefin/components'
import { TypeGuards } from '@services/type-guards'
import { useConnectWallet } from '@shared/wallet/hooks/useConnectWallet'
import useTranslation from 'next-translate/useTranslation'

import {
  ConnectRecipientWallet,
  OnChainReceiveSection,
} from '../../../../containers'

import type {
  BlockchainAbbreviation,
  BlockchainLocationMetadata,
  Maybe,
} from '@shared/graphql'

export interface ReceiveConnectedProps {
  /**
   * Chained transfer metadata.
   */
  transferMetadata?: Maybe<BlockchainLocationMetadata>
  /**
   * Destination address.
   */
  address?: string
  /**
   * Destination chain.
   */
  chain?: BlockchainAbbreviation
}

export const ReceiveConnected: React.FC<ReceiveConnectedProps> = ({
  transferMetadata,
  address,
  chain,
}) => {
  const { t } = useTranslation('ace')

  const { activeChain, isConnected } = useConnectWallet()

  const [hasWalletError, setHasWalletError] = useState(false)

  const handleWalletError = useCallback(
    (hasError: boolean) => () => {
      setHasWalletError(hasError)
    },
    [],
  )

  return (
    <>
      <Heading alignment="center" layout="vertical" variant="page/sm">
        <Heading.Header as="h2">
          {t(`sendOnchain.receive.heading`)}
        </Heading.Header>
        <Heading.Intro as="p">
          {t(`sendOnchain.receive.subheading`)}
        </Heading.Intro>
      </Heading>

      <div className="mt-12">
        {address && chain != null && (
          <ConnectRecipientWallet
            address={address}
            chain={chain}
            label={t('sendOnchain.receive.connectedWalletLabel')}
            onError={handleWalletError(true)}
            onSuccess={handleWalletError(false)}
          />
        )}

        {TypeGuards.AcceleratedChainExpansion.isSupportedChainId(
          activeChain?.id,
        ) && (
          <OnChainReceiveSection
            chainId={activeChain.id}
            hasWalletError={hasWalletError}
            isConnected={isConnected}
            transferMetadata={transferMetadata}
          />
        )}
      </div>
    </>
  )
}
